import { BaseController } from 'controllers/BaseController'
import { ProjectEventType } from 'model/ProjectEventLevel'
import { NavigateFunction } from 'react-router'
import {
  CreateProjectRequest,
  ProjectDto,
  ReCreateProjectRequest,
  SetEstimatedDeliveryDateRequest,
  SetEstimatedDeliveryDaysRequest,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { GetContext } from 'store/storeUtils'
import { ProjectsAPI } from './ProjectsAPI'

export interface IProjectsAPI {
  CreateNewProject: (args: CreateProjectRequest) => Promise<string>
  SetEstimatedDeliveryDate: (
    projectId: string,
    req: SetEstimatedDeliveryDateRequest,
  ) => Promise<void>
  SetEstimatedDeliveryDays: (
    projectId: string,
    req: SetEstimatedDeliveryDaysRequest,
  ) => Promise<void>
  GetProjectInfo: (
    projectId: string,
    reason?: ProjectEventType,
    reasonExtra?: string,
  ) => Promise<ProjectDto>
  ReCreateProject: (
    projectId: string,
    req: ReCreateProjectRequest,
  ) => Promise<{ newProjectId: string }>
  GetImageToken: (projectId: string) => Promise<{
    token: string
    expires: string
  }>
}

export class ProjectsController extends BaseController<IProjectsAPI> {
  public partyId: string

  constructor() {
    let _partyId: string

    super((onRequestChange) => {
      const { partyId } = GetContext()

      _partyId = partyId

      return new ProjectsAPI(partyId, onRequestChange)
    })

    this.partyId = _partyId
  }

  public async CreateNewProject(
    args: CreateProjectRequest,
    navigate: NavigateFunction,
  ): Promise<string> {
    try {
      const response = await this.api.CreateNewProject(args)

      if (navigate) {
        navigate(`/app/project/${encodeURI(response)}`)
      } else {
        window.location.assign(`/app/project/${encodeURI(response)}`)
      }
      return response
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async SetEstimatedDeliveryDate(
    projectId: string,
    req: SetEstimatedDeliveryDateRequest,
  ): Promise<void> {
    try {
      await this.api.SetEstimatedDeliveryDate(projectId, req)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async SetEstimatedDeliveryDays(
    projectId: string,
    req: SetEstimatedDeliveryDaysRequest,
  ): Promise<void> {
    try {
      await this.api.SetEstimatedDeliveryDays(projectId, req)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async GetProjectImageToken(projectId: string) {
    try {
      const response = await this.api.GetImageToken(projectId)
      return response
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async GetProjectInfo(
    projectId: string,
    reason?: ProjectEventType,
    reasonExtra?: string,
  ) {
    try {
      return await this.api.GetProjectInfo(projectId, reason, reasonExtra)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async ReCreateProject(
    projectId: string,
    req: ReCreateProjectRequest,
  ): Promise<string> {
    try {
      const resp = await this.api.ReCreateProject(projectId, req)

      console.info(
        `Project recreated successfully. New project URL: ${resp.newProjectId}`,
      )

      const redirectionUrl = `/app/project/${resp.newProjectId}`

      return redirectionUrl
    } catch (err) {
      throw this.HandleError(err)
    }
  }
}
